import React, { useContext, useState, useEffect, useMemo } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../context/EditSessionsContext";
import { Types, EditedSession } from "../../context/formData.reducer";
import ClassroomSection from "./classroom-section";
import Repeat from "../../components/Repeat/Repeat";
import DaysSection from "../../components/days-section";
import BlockRange from "../../components/BlockRange/BlockRange";
import FormHeader from "../../components/FormEdit/FormHeader";
import InstructorSection from "./instructor-section";
import { sessionsHaveTheSameResources } from "../../utils/sessionsHaveTheSameResources";
import { ContextApp } from "@config/Context/contextApp";
import { CardNotification } from "@foris/avocado-suite";
import { classifySessionLockState } from "../../utils/classifySessionLockState";
import { getOriginalWeeksBySessionId } from "../../utils/getOriginalWeeksBySessionId";
import { useWeekLockContext } from "../../hooks/useWeekLockContext";
import { canForkSelectedSessions } from "../../utils/canForkSelectedSessions";
import WeeksForkCard from "../../components/weeks-fork-card";
import ProposalModeBanner from "../../components/proposal-mode-banner";
import AbilitiesMessage from "./AbilitiesMessage";
import RecommendationsSection from "./recommendations-section";
import css from "./formEdit.module.scss";
import OnboardingVideoModal from "@common/components/onboarding-video-modal";
import {
  ASSIGNATION_RECOMMENDATION_ONBOARDING,
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";

const EditSession: React.FC = () => {
  const { user } = useContext(ContextApp);
  const { state, dispatch } = useContext(AppContext);
  const [currentEditedSessions, setCurrentEditedSessions] = useState<EditedSession>({});
  const [originalWeeksBySessionId, setOriginalWeeksBySessionId] = useState<{
    [key: string]: string[];
  }>({});
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showOnboardingVideo, setShowOnboardingVideo] = useState(false);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);

  const selectedSessions = state?.form?.selectedSessions;
  const abilities = user?.abilities;
  const thresholdDate = state?.form?.blockingThreshold;

  const weekLockContext = useWeekLockContext(
    originalWeeksBySessionId,
    !abilities?.can_assign_intervals,
  );
  const canEditPastSessions = weekLockContext.canEditPastSessions;

  const shouldShowForkCard = useMemo(
    () => canForkSelectedSessions(state?.form, weekLockContext, originalWeeksBySessionId),
    [state?.form, weekLockContext, originalWeeksBySessionId],
  );

  const shouldShowPastWarning = useMemo(() => {
    if (canEditPastSessions || !thresholdDate || !selectedSessions?.length) return false;

    return selectedSessions.some(
      (session: EditedSession) =>
        classifySessionLockState(session, thresholdDate, canEditPastSessions) === "mixed",
    );
  }, [selectedSessions, thresholdDate, canEditPastSessions]);

  const { t } = useTranslation();

  const showHotspot = useMemo(() => {
    return shouldShowHotspot(ASSIGNATION_RECOMMENDATION_ONBOARDING);
  }, [onboardingsSeen]);

  useEffect(() => {
    if (
      Boolean(state?.form?.editedSessions) &&
      !sessionsHaveTheSameResources(currentEditedSessions, state?.form?.editedSessions)
    ) {
      setCurrentEditedSessions(state?.form?.editedSessions);
    }
  }, [state?.form?.editedSessions]);

  useEffect(() => {
    if (Boolean(currentEditedSessions)) {
      dispatch({ type: Types.SavedSessions, payload: [currentEditedSessions] });
    }
  }, [currentEditedSessions]);

  useEffect(() => {
    if (!state?.link?.sections) return;
    setOriginalWeeksBySessionId(getOriginalWeeksBySessionId(state.link.sections));
  }, [state?.link?.sections]);

  return (
    <>
      <section className={css.formEdit}>
        <>
          {selectedSessions?.length ? (
            <div className={css.formEdit_content}>
              <section className={cx(css.fields)}>
                <section className={cx(css.fields_content)}>
                  <FormHeader />
                  <ProposalModeBanner />
                  {shouldShowPastWarning && (
                    <CardNotification
                      className={css.pastWarning}
                      state="warning"
                      title={t("assignation-edit.past-sessions.warning.title")}
                      outlined
                    >
                      {t("assignation-edit.past-sessions.warning.body")}
                    </CardNotification>
                  )}
                  <AbilitiesMessage className={css.infoMessage} />
                  <BlockRange disabled={!abilities?.can_assign_blocks} />
                  <DaysSection disabled={!abilities?.can_assign_blocks} />
                  <InstructorSection disabled={!abilities?.can_assign_instructors} />
                  <ClassroomSection disabled={!abilities?.can_assign_classrooms} />
                  <Repeat
                    disabled={!abilities?.can_assign_intervals}
                    originalWeeksBySessionId={originalWeeksBySessionId}
                  />
                  {shouldShowForkCard && <WeeksForkCard weekLockContext={weekLockContext} />}
                </section>
                <section
                  className={cx(
                    css.recommendationsContainer,
                    showRecommendations && !!state?.link && css.recommendationsContainer__open,
                  )}
                >
                  <RecommendationsSection
                    isOpen={showRecommendations}
                    isLoading={state?.link?.isLoadingRecommendations || !state?.link}
                    hasErrors={state?.link?.hasErrorsRecommendations}
                    shouldShowHotspot={showHotspot}
                    onShowOnboarding={() => {
                      setShowOnboardingVideo(true);
                      setIsOnboardingOpen(true, ASSIGNATION_RECOMMENDATION_ONBOARDING);
                    }}
                    onToggle={() => setShowRecommendations(!showRecommendations)}
                  />
                </section>
              </section>
            </div>
          ) : (
            <>
              <FormHeader />
              <ProposalModeBanner />
              <CardNotification
                state="info"
                title={t("assignation-edit.form.notifications.no-sessions-selected.title")}
                outlined
              >
                {t("assignation-edit.form.notifications.no-sessions-selected.description")}
              </CardNotification>
            </>
          )}
        </>
      </section>

      {showHotspot && (
        <OnboardingVideoModal
          isOpen={showOnboardingVideo}
          title={t("onboarding.assignation-recommendation.title")}
          description={t("onboarding.assignation-recommendation.description")}
          videoUrl="https://files.foris.ai/darwin/onboarding/Recomendador+de+Asignacio%CC%81n+-+PROD.mp4"
          onClose={() => {
            setShowOnboardingVideo(false);
            setIsOnboardingOpen(false, ASSIGNATION_RECOMMENDATION_ONBOARDING);
          }}
        />
      )}
    </>
  );
};

export default EditSession;
